function positiveSafeInteger(value, fieldName) {
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number <= 0) {
        throw new TypeError(`${fieldName} must be a positive safe integer`);
    }
    return number;
}

function nonNegativeSafeInteger(value, fieldName) {
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 0) {
        throw new TypeError(`${fieldName} must be a non-negative safe integer`);
    }
    return number;
}

function safeAdd(left, right, fieldName) {
    const result = left + right;
    if (!Number.isSafeInteger(result)) {
        throw new TypeError(`${fieldName} must be a safe integer`);
    }
    return result;
}

export function addProductQuantities(left, right) {
    return safeAdd(
        nonNegativeSafeInteger(left, "quantity"),
        positiveSafeInteger(right, "quantity"),
        "quantity"
    );
}

export function aggregateOriginalQuantities(products) {
    return products.reduce((quantities, product) => {
        const externalId = positiveSafeInteger(product.external_id, "external_id");
        const quantity = positiveSafeInteger(product.quantity, "quantity");
        quantities[externalId] = safeAdd(
            quantities[externalId] || 0,
            quantity,
            "original_quantity"
        );
        return quantities;
    }, {});
}

export function deepFreeze(value) {
    if (value === null || typeof value !== "object" || Object.isFrozen(value)) {
        return value;
    }
    Object.values(value).forEach(deepFreeze);
    return Object.freeze(value);
}

export function snapshotProducts(products, {includeOriginalQuantity = false} = {}) {
    return Object.freeze(products.map(product => Object.freeze({
        ...product,
        external_id: positiveSafeInteger(product.external_id, "external_id"),
        quantity: positiveSafeInteger(product.quantity, "quantity"),
        original_quantity: includeOriginalQuantity && product.original_quantity != null
            ? nonNegativeSafeInteger(product.original_quantity, "original_quantity")
            : 0
    })));
}

export function snapshotRecipients(recipients) {
    return Object.freeze(recipients.map(recipient => Object.freeze({
        ...recipient,
        address_id: positiveSafeInteger(recipient.address_id, "address_id"),
        buyer_id: positiveSafeInteger(recipient.buyer_id, "buyer_id")
    })));
}

export function snapshotValue(value) {
    return deepFreeze(JSON.parse(JSON.stringify(value)));
}

export function snapshotShoppingCartData(userId, warehouseId, products, recipients) {
    const warehouseData = {_recipients: recipients.map(recipient => ({...recipient}))};
    products.forEach(product => {
        warehouseData[product.external_id] = {...product};
    });

    return deepFreeze({
        [userId]: {
            [warehouseId]: warehouseData
        }
    });
}

export function aggregateProducts(productGroups) {
    const productsByExternalId = {};

    productGroups.forEach(products => {
        products.forEach(product => {
            const externalId = positiveSafeInteger(product.external_id, "external_id");
            const quantity = positiveSafeInteger(product.quantity, "quantity");
            const originalQuantity = product.original_quantity == null
                ? 0
                : nonNegativeSafeInteger(product.original_quantity, "original_quantity");

            if (!productsByExternalId[externalId]) {
                productsByExternalId[externalId] = {
                    product_external_id: externalId,
                    product_name: product.name,
                    quantity_to_order: 0,
                    original_quantity: 0
                };
            }

            productsByExternalId[externalId].quantity_to_order = safeAdd(
                productsByExternalId[externalId].quantity_to_order,
                quantity,
                "quantity_to_order"
            );
            productsByExternalId[externalId].original_quantity = safeAdd(
                productsByExternalId[externalId].original_quantity,
                originalQuantity,
                "original_quantity"
            );
        });
    });

    return Object.freeze(Object.values(productsByExternalId).map(product => Object.freeze(product)));
}

export async function findUnavailableProducts(warehouseId, products, api) {
    const unavailableProducts = [];
    const params = {};
    const localizationLanguage = process.env.VUE_APP_LOCALIZATION_LANUGUAGE;
    if (localizationLanguage) {
        params.localizationLanguage = localizationLanguage;
    }

    for (const product of products) {
        const response = await api.get(
            `/api/warehouses/${warehouseId}/products/${product.product_external_id}/in-stock`,
            {params}
        );
        const quantityInStock = nonNegativeSafeInteger(response.data.in_stock, "in_stock");
        const availableQuantity = safeAdd(
            quantityInStock,
            product.original_quantity,
            "available_quantity"
        );
        if (availableQuantity < product.quantity_to_order) {
            unavailableProducts.push(Object.freeze({
                ...product,
                quantity_in_stock: quantityInStock
            }));
        }
    }

    return Object.freeze(unavailableProducts);
}

export function formatUnavailableProductsAlert(title, unavailableProducts) {
    const details = unavailableProducts.map(product => (
        `- ${product.product_name}: k objednání ${product.quantity_to_order}, `
        + `na skladě ${product.quantity_in_stock}`
    ));
    return [title, ...details].join("\n");
}
